import { ThemeProvider } from '@mui/material';
import log from 'loglevel';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';
import '@/App.css';
import EricksonAuthenticator from '@/auth/EricksonAuthenticator';
import { MessageQueueProvider } from '@erickson/components';
import DashboardPage from '@/pages/dashboardPage/DashboardPage.tsx';
import theme from '@/theme';
import BoughListFilterPage from '@/pages/BoughtListFilterPage';
import HouseListFilterPage from '@/pages/HouseListFilterPage';
import Navbar from '@/components/Navbar/Navbar';
import Breadcrumb from '@/components/Breadcrumb';
import BoughtListOptOutPage from '@/pages/BoughtListOptOutPage/BoughtListOptOutPage.tsx';
import { ServiceProvider } from '@/components/ServiceContext';
import { CustomRouteObject } from '@/service/RouteService.ts';
import Container from '@mui/material/Container';
import ExportPreviewPage from '@/pages/ExportPreviewPage/ExportPreviewPage.tsx';
import DoNotMailReport from '@/pages/Reports/BoughtList/DoNotMailReport.tsx';
import ReportsPage from '@/pages/Reports/ReportsPage.tsx';
import DepositorPurgeReport from '@/pages/Reports/HouseList/DepositorPurgeReport.tsx';
import CCHouseListReport from '@/pages/Reports/HouseList/CCHouseListReport.tsx';
import CountsByGradeReport from '@/pages/Reports/BoughtList/CountsByGradeReport.tsx';
import ListNameExpirationCountReport from '@/pages/Reports/BoughtList/ListNameExpirationCountReport.tsx';
import DownloadExport from '@/pages/Reports/DownloadExport.tsx';
import SessionHandler from '@/components/SessionHandler/SessionHandler.tsx';
import AdminPage from '@/pages/Admin/AdminPage.tsx';
import ExclusionRulesPage from '@/pages/Admin/ExclusionRulesPage.tsx';
import MLInactiveFlagsPage from '@/pages/Admin/MLInactiveFlagsPage.tsx';
import FlagDictionaryPage from '@/pages/Admin/FlagDictionaryPage.tsx';
import FileManagerPage from '@/pages/Admin/FileManagerPage.tsx';
import { FileManagerRouteProtection } from '@/components/RouteProtection';
import DataTransferJobsPage from '@/pages/DataLoadPage.tsx';
import Redirector from '@/components/Redirector.tsx';
import EnvHeader from '@/components/EnvHeader.tsx';
import DataExplorerPage from './pages/DataExplorerPage/DataExplorerPage';
import StaffSeedReport from './pages/Reports/StaffSeedReport';
import AcxoimDoNotMailReport from '@/pages/Reports/BoughtList/AcxoimDoNotMailReport.tsx';
import ExclusionCacheInitializer from '@/components/ExclusionCacheInitializer';

log.setLevel(log.levels.INFO);

log.info('---- List Logic Portal ----');
log.info('Version:\t', __APP_VERSION__);
log.info('Branch:\t\t', __BRANCH_NAME__);
log.info('Commit:\t\t', __COMMIT_HASH__);
log.info('Local Env:\t', __LOCAL_ENV_NAME__);

const routes = [
  {
    id: 'home',
    path: '/',
    label: 'Home',
    element: <Navigate to="/houselist" />,
  },
  {
    path: '/admin',
    element: <Navigate to="/admin/user-management" replace />,
  },
  {
    id: 'admin',
    path: '/admin/user-management',
    label: 'User Management',
    element: <AdminPage />,
  },
  {
    id: 'exclusion-rules',
    path: '/admin/exclusion-rules',
    label: 'Exclusion Rules',
    element: <ExclusionRulesPage />,
  },
  {
    id: 'ml-inactive-flags',
    path: '/admin/ml-inactive-flags',
    label: 'ML Inactive Flags',
    element: <MLInactiveFlagsPage />,
  },
  {
    id: 'flag-dictionary',
    path: '/admin/flag-dictionary',
    label: 'Flag Dictionary',
    element: <FlagDictionaryPage />,
  },
  {
    id: 'file-manager',
    path: '/admin/file-manager',
    label: 'File Manager',
    element: (
      <FileManagerRouteProtection>
        <FileManagerPage />
      </FileManagerRouteProtection>
    ),
  },
  {
    id: 'find-resident',
    path: '/findresident',
    label: 'Find Resident',
    element: <DashboardPage />,
  },
  {
    id: 'disclaimer',
    path: '/disclaimer',
    label: 'Disclaimer',
    element: <DashboardPage />,
  },
  {
    id: 'bought-list',
    path: '/boughtlist',
    label: 'Bought List',
    element: <BoughListFilterPage />,
  },
  {
    id: 'boughtlist-export-preview',
    path: '/boughtlist/export-preview',
    label: 'Export Preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'bought-list-opt-out',
    path: '/boughtlist/opt-out',
    label: 'Opt Out',
    element: <BoughtListOptOutPage key="opt-out" />,
  },
  {
    id: 'bought-list-opt-out-search',
    path: '/boughtlist/opt-out/search',
    label: 'Opt Out',
    element: <BoughtListOptOutPage key="opt-out-search" />,
  },
  {
    id: 'house-list',
    path: '/houselist',
    label: 'House List',
    element: <HouseListFilterPage />,
  },
  {
    id: 'houselist-export-preview',
    path: '/houselist/export-preview',
    label: 'Export Preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'wound-form',
    path: '/WoundFormPage',
    label: 'Wound Form',
    element: <DashboardPage />,
  },
  {
    id: 'reports',
    path: '/report',
    label: 'Reports',
    element: <ReportsPage />,
  },
  {
    id: 'reports-bl',
    path: '/report/boughtlist',
    label: 'Bought List Reports',
    element: <ReportsPage listType={'boughtlist'} />,
  },
  {
    id: 'report-bl-counts-by-grade',
    path: '/report/boughtlist/counts-by-grade',
    element: <CountsByGradeReport />,
  },
  {
    id: 'report-bl-counts-by-grade-export-preview',
    path: '/report/boughtlist/counts-by-grade/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'report-bl-do-not-mail',
    path: '/report/boughtlist/do-not-mail',
    element: <DoNotMailReport />,
  },
  {
    id: 'report-bl-do-not-mail-export-preview',
    path: '/report/boughtlist/do-not-mail/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'report-bl-acxiom-do-not-mail',
    path: '/report/boughtlist/acxiom-do-not-mail',
    element: <AcxoimDoNotMailReport />,
  },
  {
    id: 'report-bl-acxiom-do-not-mail-export-preview',
    path: '/report/boughtlist/acxiom-do-not-mail/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'report-bl-list-name-expiration-count',
    path: '/report/boughtlist/list-name-expiration-count',
    element: <ListNameExpirationCountReport />,
  },
  {
    id: 'report-bl-list-name-expiration-count-export-preview',
    path: '/report/boughtlist/list-name-expiration-count/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'reports-hl',
    path: '/report/houselist',
    label: 'House List Reports',
    element: <ReportsPage listType={'houselist'} />,
  },
  {
    id: 'report-hl-depositor-purge',
    path: '/report/houselist/depositor-purge',
    element: <DepositorPurgeReport />,
  },
  {
    id: 'report-hl-depositor-purge-export-preview',
    path: '/report/houselist/depositor-purge/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'report-hl-cc-house-list',
    path: '/report/houselist/cc-house-list',
    element: <CCHouseListReport />,
  },
  {
    id: 'report-hl-cc-house-list-export-preview',
    path: '/report/houselist/cc-house-list/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    id: 'report-staff-seed',
    path: '/report/staffseed',
    element: <StaffSeedReport />,
  },
  {
    id: 'report-staff-seed-export-preview',
    path: '/report/staffseed/export-preview',
    element: <ExportPreviewPage />,
  },
  {
    path: '/export',
    label: 'Export',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/export/:exportId',
    label: 'Export Details',
    element: <Navigate to="/" replace />,
  },
  {
    id: 'export-download',
    path: '/export/:exportId/download',
    label: 'Download Export',
    element: <DownloadExport />,
  },
  {
    id: 'data-transfer',
    path: '/admin/data-transfer',
    label: 'Data Transfer Log',
    element: <DataTransferJobsPage />,
  },
  {
    id: 'data-explorer',
    path: '/admin/data-explorer',
    label: 'Data Explorer',
    element: <DataExplorerPage />,
  },
] as CustomRouteObject[];

const Layout = () => {
  return (
    <>
      <Redirector />
      <EnvHeader />
      <Navbar />
      <Container
        sx={{ maxHeight: 12, bgcolor: theme.palette.secondary.main }}
        maxWidth={false}
      >
        &nbsp;
      </Container>
      <Breadcrumb />
      <main>
        <Outlet />
      </main>
    </>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: routes,
  },
] as CustomRouteObject[]);

function App() {
  return (
    <MessageQueueProvider>
      <EricksonAuthenticator customProvider="Erickson">
        <SessionHandler />
        <ThemeProvider theme={theme}>
          <ServiceProvider routes={routes}>
            <ExclusionCacheInitializer>
              <RouterProvider router={router} />
            </ExclusionCacheInitializer>
          </ServiceProvider>
        </ThemeProvider>
      </EricksonAuthenticator>
    </MessageQueueProvider>
  );
}

export default App;
